import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLanguage } from '../../../../../../contexts/LanguageContext';
import { selectUserName } from '../../../../../../features/auth/authSlice';
import toastService from '../../../../../../utils/toastService';
import {
    useGetWarrantyAuditTemplateQuery,
    useGetWarrantyAuditScoreBandsQuery,
    useGetWarrantyAuditResultByIdQuery,
    useCreateWarrantyAuditResultMutation
} from '../../../../../../api/endpoints/warrantyAudit';

// Toplam puana karşılık gelen sonuç bandını bul
export const resolveBand = (bands, totalScore) => {
    if (!Array.isArray(bands)) return null;
    return bands.find((b) => totalScore >= b.minScore && totalScore <= b.maxScore) || null;
};

const useAuditForm = (id) => {
    const isEdit = Boolean(id);
    const { th, getCurrentLanguageId } = useLanguage();
    const languageId = getCurrentLanguageId();
    const userName = useSelector(selectUserName);

    const [auditDate, setAuditDate] = useState(new Date());
    const [bosBranchCode, setBosBranchCode] = useState(null);
    const [scores, setScores] = useState({}); // { [subCriteriaId]: number }
    const [submitted, setSubmitted] = useState(false);
    const [savedResult, setSavedResult] = useState(null);

    const { data: template = [], isLoading: isTemplateLoading } = useGetWarrantyAuditTemplateQuery({ languageId });
    const { data: scoreBands = [], isLoading: isBandsLoading } = useGetWarrantyAuditScoreBandsQuery({ languageId });
    const { data: existingResult, isLoading: isResultLoading } = useGetWarrantyAuditResultByIdQuery(
        { id, languageId },
        { skip: !isEdit }
    );

    const [createResult, { isLoading: isSaving }] = useCreateWarrantyAuditResultMutation();

    // Düzenleme modunda mevcut sonucu state'e yükle
    useEffect(() => {
        if (isEdit && existingResult) {
            setSavedResult(existingResult);
            setAuditDate(existingResult.auditDate ? new Date(existingResult.auditDate) : new Date());
            setBosBranchCode(existingResult.bosBranchCode || null);
            const scoreMap = {};
            (existingResult.details || []).forEach((d) => {
                scoreMap[d.subCriteriaId] = d.score;
            });
            setScores(scoreMap);
        }
    }, [isEdit, existingResult]);

    // Alt kriter -> maxScore eşlemesi
    const subCriteriaMaxMap = useMemo(() => {
        const map = {};
        template.forEach((c) => (c.subCriteria || []).forEach((s) => (map[s.id] = s.maxScore ?? 0)));
        return map;
    }, [template]);

    const maxTotalScore = useMemo(
        () => Object.values(subCriteriaMaxMap).reduce((acc, v) => acc + (Number(v) || 0), 0),
        [subCriteriaMaxMap]
    );

    const totalScore = useMemo(
        () => Object.values(scores).reduce((acc, v) => acc + (Number(v) || 0), 0),
        [scores]
    );

    const currentBand = useMemo(() => {
        if (savedResult) {
            return {
                text: savedResult.scoreBandText,
                color: savedResult.scoreBandColor,
                code: savedResult.scoreBandCode
            };
        }
        const band = resolveBand(scoreBands, totalScore);
        return band ? { text: band.text, color: band.color, code: band.code } : null;
    }, [savedResult, scoreBands, totalScore]);

    const setScore = useCallback(
        (subCriteriaId, value) => {
            const max = subCriteriaMaxMap[subCriteriaId] ?? Infinity;
            let next = value == null ? null : Number(value);
            if (next != null && next > max) next = max;
            if (next != null && next < 0) next = 0;
            setScores((prev) => ({ ...prev, [subCriteriaId]: next }));
        },
        [subCriteriaMaxMap]
    );

    const validate = useCallback(() => {
        if (!bosBranchCode) {
            toastService.warn(th('warrantyAudit.validation.dealerRequired') || 'Bayi/Servis seçiniz');
            return false;
        }
        // Her alt kriter için puan girilmiş olmalı
        const allSubIds = Object.keys(subCriteriaMaxMap);
        const missing = allSubIds.some((sid) => scores[sid] == null);
        if (missing) {
            toastService.warn(th('warrantyAudit.validation.scoresRequired') || 'Tüm alt kriterler için puan giriniz');
            return false;
        }
        return true;
    }, [bosBranchCode, subCriteriaMaxMap, scores, th]);

    const handleSave = useCallback(async () => {
        setSubmitted(true);
        if (!validate()) return null;

        const details = Object.entries(scores).map(([subCriteriaId, score]) => ({
            subCriteriaId: Number(subCriteriaId),
            score: Number(score) || 0
        }));

        const payload = {
            auditDate: auditDate ? auditDate.toISOString() : null,
            bosBranchCode: String(bosBranchCode),
            bosBranchSiteCode: String(bosBranchCode),
            languageId,
            details
        };

        try {
            const result = await createResult(payload).unwrap();
            setSavedResult(result);
            toastService.success(th('warrantyAudit.toast.saveSuccess') || 'Denetim kaydedildi');
            return result;
        } catch (error) {
            toastService.error(th('warrantyAudit.toast.saveError') || 'Kayıt sırasında hata oluştu');
            return null;
        }
    }, [validate, scores, auditDate, bosBranchCode, languageId, createResult, th]);

    return {
        isEdit,
        isLoading: isTemplateLoading || isBandsLoading || (isEdit && isResultLoading),
        isSaving,
        template,
        scoreBands,
        scores,
        setScore,
        subCriteriaMaxMap,
        maxTotalScore,
        totalScore,
        currentBand,
        auditDate,
        setAuditDate,
        bosBranchCode,
        setBosBranchCode,
        auditorName: savedResult?.auditorName || userName,
        savedResult,
        submitted,
        handleSave
    };
};

export default useAuditForm;
